import React from "react";
import { getFullFileUrl } from "../../Helpers/UrlHelpers";
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from "framer-motion";
import {
    faStar,
    faGraduationCap,
    faArrowLeft,
    faUser,
    faChalkboardTeacher,
    faQuoteRight,
    faCrown
} from '@fortawesome/free-solid-svg-icons';

const fadeUp = {
    hidden: { opacity: 0, y: 32 },
    visible: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
    }),
};

const OurTeachers = ({ best_teachers, apiHost }) => {
    const navigate = useNavigate();
    const teachers = best_teachers || [];
    const handleTeacherClick = (teacherId) => {
        navigate(`/teacher-profile/${teacherId}`);
    };

    return (
        <section className="tc-root" id="teachers">
            <div className="tc-container">
                {/* ── header ── */}
                <motion.div
                    className="tc-header"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <motion.div className="tc-eyebrow" variants={fadeUp} custom={0}>
                        <span className="tc-eyebrow-dot" />
                        نخبة المعلمين
                    </motion.div>
                    <motion.h2 className="tc-title" variants={fadeUp} custom={1}>
                        اصنع مستقبلك مع <em>أفضل الخبراء</em>
                    </motion.h2>
                    <motion.p className="tc-subtitle" variants={fadeUp} custom={2}>
                        اكتشف معلمين متميزين يساعدونك في رحلة التفوق والنجاح الأكاديمي
                    </motion.p>
                </motion.div>

                {/* ── grid ── */}
                <motion.div
                    className="tc-grid"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                >
                    {teachers.map((teacher, index) => (
                        <motion.article
                            key={teacher.id}
                            className="tc-card"
                            variants={fadeUp}
                            custom={index}
                            onClick={() => handleTeacherClick(teacher.id)}
                            whileHover="hover"
                            initial="rest"
                            animate="rest"
                        >
                            {/* image */}
                            <div className="tc-img-box">
                                <img
                                    src={getFullFileUrl(teacher.image)}
                                    alt={teacher.full_name}
                                    className="tc-img"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                                <div className="tc-img-placeholder">
                                    <FontAwesomeIcon icon={faUser} />
                                </div>
                                <div className="tc-img-overlay" />
                                <div className="tc-rating">
                                    <FontAwesomeIcon icon={faStar} className="tc-star" />
                                    <span>{teacher.rate}</span>
                                </div>
                                {teacher.is_vip && (
                                    <div className="tc-vip-badge">
                                        <FontAwesomeIcon icon={faCrown} />
                                        <span>VIP</span>
                                    </div>
                                )}
                            </div>

                            {/* body */}
                            <div className="tc-body">
                                <span className="tc-qual">
                                    <FontAwesomeIcon icon={faGraduationCap} />
                                    {teacher.qualification || "معلم خبير"}
                                </span>

                                <h3 className="tc-name">{teacher.full_name}</h3>

                                {teacher.material && (
                                    <p className="tc-subject">{teacher.material}</p>
                                )}

                                <div className="tc-divider" />

                                <div className="tc-footer">
                                    <div className="tc-stats">
                                        <span className="tc-count">{teacher.student_count || 0}+</span>
                                        <span className="tc-label">طالب</span>
                                    </div>
                                    <motion.button
                                        className="tc-btn"
                                        whileHover={{ x: -4 }}
                                        transition={{ type: 'spring', stiffness: 300 }}
                                    >
                                        <span>الملف الشخصي</span>
                                        <FontAwesomeIcon icon={faArrowLeft} />
                                    </motion.button>
                                </div>
                            </div>

                            {/* accent bar */}
                            <motion.div
                                className="tc-card-bar"
                                variants={{ rest: { opacity: 0 }, hover: { opacity: 1 } }}
                                transition={{ duration: 0.3 }}
                            />
                        </motion.article>
                    ))}
                </motion.div>

                {teachers.length === 0 && (
                    <div className="tc-empty">
                        <h3>لا يوجد معلمين متاحين حالياً</h3>
                    </div>
                )}
            </div>

            <style>{`
                /* ── root ── */
                .tc-root {
                      font-family: "Zain", sans-serif;
                    direction: rtl;
                    position: relative;
                    padding: 120px 0 100px;
                    background: #fff;
                    overflow: hidden;
                    isolation: isolate;
                }

                /* ── container ── */
                .tc-container {
                    position: relative; z-index: 2;
                    max-width: 1260px; margin: 0 auto; padding: 0 40px;
                }
                @media(min-width:1200px){ .tc-container { padding: 0 60px; } }

                /* ── header ── */
                .tc-header { text-align: center; margin-bottom: 72px; }

                .tc-eyebrow {
                    display: inline-flex; align-items: center; gap: 10px;
                    color: #6366f1; font-weight: 800; font-size: 0.82rem;
                    letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 22px;
                }
                .tc-eyebrow-dot {
                    width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
                    animation: tc-pulse 2.2s ease-in-out infinite;
                }
                @keyframes tc-pulse {
                    0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.45); }
                    50%{ box-shadow: 0 0 0 9px rgba(232,146,10,0); }
                }
                .tc-title {
                    font-size: clamp(2.2rem, 4vw, 3.6rem);
                    font-weight: 900; color: #0f172a;
                    margin: 0 0 18px; line-height: 1.2;
                    letter-spacing: -0.025em;
                }
                .tc-title em {
                    font-style: normal;
                    background: linear-gradient(90deg, #6366f1 0%, #6366f1 55%, #6366f1 100%);
                    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
                .tc-subtitle {
                    font-size: 1.08rem; color: #64748b; line-height: 1.85; margin: 0;
                }

                /* ── grid ── */
                .tc-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 20px;
                }
                @media(max-width:1080px){ .tc-grid { grid-template-columns: repeat(2,1fr); } }
                @media(max-width:640px){ .tc-grid { grid-template-columns: 1fr; } }

                /* ── card ── */
                .tc-card {
                    position: relative;
                    border-radius: 24px;
                    border: 1px solid rgba(15,23,42,0.08);
                    background: #fff;
                    overflow: hidden;
                    cursor: pointer;
                    display: flex;
                    flex-direction: column;
                    transition: border-color 0.3s, box-shadow 0.3s;
                }
                .tc-card:hover {
                    border-color: rgba(15,23,42,0.15);
                    box-shadow: 0 16px 40px rgba(15,23,42,0.1);
                }

                /* image */
                .tc-img-box {
                    position: relative;
                    height: 260px;
                    overflow: hidden;
                    background: #f1f5f9;
                }
                .tc-img {
                    width: 100%; height: 100%;
                    object-fit: cover;
                    transition: transform 0.7s ease;
                }
                .tc-card:hover .tc-img { transform: scale(1.07); }

                .tc-img-placeholder {
                    display: none;
                    width: 100%; height: 100%;
                    align-items: center; justify-content: center;
                    color: #cbd5e1; font-size: 4rem;
                }
                .tc-img-overlay {
                    position: absolute; bottom: 0; left: 0; right: 0;
                    height: 45%;
                    background: linear-gradient(to top, rgba(15,23,42,0.5), transparent);
                }
                .tc-rating {
                    position: absolute; top: 16px; right: 16px;
                    background: rgba(255,255,255,0.95);
                    backdrop-filter: blur(6px);
                    padding: 5px 12px; border-radius: 10px;
                    display: flex; align-items: center; gap: 6px;
                    font-weight: 800; font-size: 0.9rem; color: #0f172a;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                }
                .tc-vip-badge {
                    position: absolute; top: 16px; left: 16px;
                    background: linear-gradient(135deg, #6366f1 0%, #d97706 100%);
                    color: #fff;
                    padding: 5px 12px; border-radius: 10px;
                    display: flex; align-items: center; gap: 6px;
                    font-weight: 800; font-size: 0.9rem;
                    box-shadow: 0 4px 15px rgba(245, 158, 11, 0.4);
                    z-index: 2;
                }
                .tc-star { color: #6366f1; }

                /* body */
                .tc-body {
                    padding: 24px 22px 20px;
                    display: flex; flex-direction: column; gap: 0;
                    flex: 1;
                }
                .tc-qual {
                    display: inline-flex; align-items: center; gap: 6px;
                    background: rgba(232,146,10,0.08);
                    color: #6366f1;
                    padding: 5px 12px; border-radius: 8px;
                    font-size: 0.8rem; font-weight: 700;
                    margin-bottom: 14px;
                }
                .tc-name {
                    font-size: 1.35rem; font-weight: 900;
                    color: #0f172a; margin: 0 0 6px; line-height: 1.3;
                }
                .tc-subject {
                    font-size: 0.95rem; color: #64748b;
                    font-weight: 500; margin: 0;
                }
                .tc-divider {
                    height: 1px; background: rgba(15,23,42,0.06);
                    margin: 18px 0;
                }

                /* footer */
                .tc-footer {
                    display: flex; align-items: center; justify-content: space-between;
                    margin-top: auto;
                }
                .tc-stats {
                    display: flex; flex-direction: column;
                }
                .tc-count {
                    font-size: 1.15rem; font-weight: 900; color: #0f172a;
                }
                .tc-label {
                    font-size: 0.78rem; color: #94a3b8; font-weight: 600;
                }
                .tc-btn {
                    display: inline-flex; align-items: center; gap: 8px;
                    background: transparent;
                    border: 1px solid #6366f1;  
                    border-radius: 10px; padding: 9px 16px;
                    font-family: inherit; font-weight: 700; font-size: 0.88rem;
                    color: #6366f1; cursor: pointer;
                    transition: background 0.2s;
                }
                .tc-btn:hover { background: rgba(232,146,10,0.06); }

                /* accent bar */
                .tc-card-bar {
                    height: 3px; width: 100%;
                    background: #6366f1;
                    flex-shrink: 0;
                }

                /* empty */
                .tc-empty {
                    text-align: center; color: #94a3b8;
                    padding: 60px 0; font-size: 1.1rem;
                }

                @media(max-width:768px){
                    .tc-root { padding: 90px 0 64px; }
                    .tc-container { padding: 0 20px; }
                    .tc-header { margin-bottom: 52px; }
                    .tc-img-box { height: 220px; }
                }
            `}</style>
        </section>
    );
};

export default OurTeachers;
